import {Component, inject, OnInit, signal} from '@angular/core';
import {DatePipe} from '@angular/common';
import {HttpErrorResponse} from '@angular/common/http';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {Button} from 'primeng/button';
import {Card} from 'primeng/card';
import {ProgressSpinner} from 'primeng/progressspinner';
import {AuthService} from '@core/services/auth.service';
import {ProcessoSeiService} from '@core/services/processo-sei.service';
import {ProcessoSei} from '@core/models/processo-sei';

/**
 * "Abrir no SisNot" do SEI (extensão SisNot ↔ SEI): /abrir-no-sisnot?processo=134.00054956/2026-16.
 * Já tem notificação com esse processo: abre a edição (mais de uma: escolhe na
 * lista). Não tem: abre o cadastro com o número já preenchido. Igual ao "Abrir no SIGEP".
 */
@Component({
  selector: 'app-abrir-no-sisnot',
  imports: [Button, Card, ProgressSpinner, RouterLink, DatePipe],
  template: `
    <div class="flex justify-center pt-10">
      <p-card styleClass="w-full max-w-xl">
        @if (varias().length) {
          <div class="header-title-table mb-1">Processo {{ numero }}</div>
          <p class="text-sm text-gray-500 dark:text-gray-400 mb-3">Há {{ varias().length }} notificações com este processo. Escolha qual abrir:</p>
          <div class="flex flex-col gap-2">
            @for (n of varias(); track n.id) {
              <a [routerLink]="['/controle-notificacoes/notificacao', n.id]"
                 class="block rounded-lg border border-gray-200 dark:border-zinc-800 px-3 py-2 hover:border-[#E41B2E] transition-colors">
                <div class="font-semibold">{{ n.numeroNotificacao || 'Sem nº NOT' }} · {{ n.concessionaria || '—' }}</div>
                <div class="text-xs text-gray-500 dark:text-gray-400">
                  {{ n.dataNotificacao | date: 'dd/MM/yyyy' }} · {{ n.status || '—' }} · {{ n.fase || '—' }}
                </div>
              </a>
            }
            <a [routerLink]="['/controle-notificacoes/nova-notificacao']" [queryParams]="{processo: numero}"
               class="text-sm underline mt-1">Cadastrar outra notificação com este processo</a>
          </div>
        } @else if (erro()) {
          <div class="header-title-table mb-1">Não deu para abrir no SisNot</div>
          <p class="text-sm text-gray-600 dark:text-gray-300">{{ erro() }}</p>
          @if (precisaEntrar()) {
            <p-button label="Entrar no SisNot (abre numa aba)" icon="pi pi-sign-in" styleClass="btn-red mt-3" (onClick)="entrar()"/>
          } @else {
            <a routerLink="/controle-notificacoes" class="text-sm underline mt-3 inline-block">Ir para o Controle de Notificações</a>
          }
        } @else {
          <div class="flex items-center gap-3">
            <p-progress-spinner styleClass="!w-8 !h-8" strokeWidth="6"/>
            <span>Procurando o processo {{ numero }} no SisNot…</span>
          </div>
        }
      </p-card>
    </div>
  `,
})
export class AbrirNoSisnot implements OnInit {
  static readonly DEPOIS_DO_LOGIN = 'sisnot:depois-do-login';
  private static readonly NUMERO_SEI = /^\d{3}\.\d{7,8}\/\d{4}-\d{2}$/;

  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private auth = inject(AuthService);
  private service = inject(ProcessoSeiService);

  protected numero = '';
  protected varias = signal<ProcessoSei[]>([]);
  protected erro = signal<string | null>(null);
  protected precisaEntrar = signal(false);

  protected entrar() {
    this.auth.login();
  }

  ngOnInit() {
    this.numero = (this.route.snapshot.queryParamMap.get('processo') || '').trim();
    if (!AbrirNoSisnot.NUMERO_SEI.test(this.numero)) {
      this.erro.set('O número do processo não está no formato do SEI.');
      return;
    }
    // sem login: entra e volta pra cá (o login do SisNot sempre voltava pra tela inicial)
    if (!this.auth.isAuthenticated() && window.top !== window) {
      // no painel lateral (extensão): o login não abre em quadro e aba aberta
      // sem clique é bloqueada pelo Chrome; a pessoa clica pra entrar numa aba
      this.erro.set('Você não está logado no SisNot neste Chrome. Entre numa aba e depois recarregue o painel (↻ no topo).');
      this.precisaEntrar.set(true);
      return;
    }
    if (!this.auth.isAuthenticated()) {
      try { sessionStorage.setItem(AbrirNoSisnot.DEPOIS_DO_LOGIN, this.router.url); } catch { /* sem storage */ }
      this.auth.login();
      return;
    }
    this.service.porNumero(this.numero).subscribe({
      next: lista => {
        if (lista.length === 0) {
          this.router.navigate(['/controle-notificacoes/nova-notificacao'], {queryParams: {processo: this.numero}, replaceUrl: true});
        } else if (lista.length === 1) {
          this.router.navigate(['/controle-notificacoes/notificacao', lista[0].id], {replaceUrl: true});
        } else {
          this.varias.set(lista);
        }
      },
      error: (e: HttpErrorResponse) => this.erro.set(e.status === 403
        ? 'A integração com o SEI não está liberada para você. Peça a um administrador do SisNot (Administração → Usuários).'
        : 'O SisNot não respondeu. Tente de novo em instantes.'),
    });
  }
}
